import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  listFamilyAnnouncements,
  reactEduAnnouncement,
  markEduAnnouncementRead,
  type FamilyAnnouncementItem,
} from '../../services/eduAnnouncementService'
import {
  listFamilyFeed,
  reactEduFeedPost,
  markEduFeedPostSeen,
  type FamilyFeedItem,
} from '../../services/eduFeedService'
import {
  listEduThreads,
  listEduThreadMessages,
  listEduMessageableTeachers,
  openEduThread,
  sendEduThreadMessage,
  editEduThreadMessage,
  deleteEduThreadMessage,
  markEduThreadRead,
  uploadThreadMessageFiles,
  generateEduRequestId,
  type EduThread,
  type EduThreadMessage,
  type MessageableTeacher,
} from '../../services/eduMessagingService'
import type { FamilyLearnerRow } from '../../services/eduFamilyService'
import {
  AppDialog,
  Button,
  ConfirmActionModal,
  FamilyCard,
  MiniLoader,
  Radio,
  ReactionBar,
  SegmentedControl,
  StatusChip,
} from '../Common'
import { mapEduServerError, eduServerErrorCode } from '../Education/classesBuilderHelpers'
import {
  getEduMediaConsent,
  setEduMediaConsent,
  type MediaConsentLevel,
} from '../../services/eduMediaConsentService'
import {
  listFamilyEduEvents,
  getFamilyEduEvent,
  type FamilyEduEventItem,
} from '../../services/eduEventService'
import {
  listFamilyEduPtmDays,
  listFamilyEduPtmTeachers,
  type EduPtmDayItem,
} from '../../services/eduPtmService'
import {
  FamilyPtmBookingDialog,
  type OtherChildBooking,
} from './FamilyPtmBookingDialog'
import { utcIsoToZonedParts, DEFAULT_EDU_TIMEZONE } from '../../utils/eduZonedTime'
import { formatEduDate, formatEduDateTime } from '../../utils/eduFamilyFormat'
import { FamilyEventDetailDialog } from './FamilyEventDetailDialog'
import { useNls } from '../../nls/nlsProvider'
import { useAuth } from '../../context/useAuth'
import './familyClassTab.css'

export type FamilyClassSubTab = 'announcements' | 'feed' | 'messages' | 'events' | 'meetings'


export interface FamilyClassTabProps {
  workspaceId: string
  learnerId: string
  learner?: FamilyLearnerRow
  otherLearners?: FamilyLearnerRow[]
  frozen?: boolean
  initialSubTab?: FamilyClassSubTab
  initialAnnouncementId?: string | null
  initialFeedPostId?: string | null
  initialThreadId?: string | null
  initialEventId?: string | null
  initialPtmDayId?: string | null
  onCountsChange?: (counts: {
    unreadAnnouncements: number
    newFeedPosts: number
    unreadMessages: number
  }) => void
}

export const FamilyClassTab: React.FC<FamilyClassTabProps> = ({
  workspaceId,
  learnerId,
  learner,
  otherLearners,
  frozen = false,
  initialSubTab = 'announcements',
  initialAnnouncementId = null,
  initialFeedPostId = null,
  initialThreadId = null,
  initialEventId = null,
  initialPtmDayId = null,
  onCountsChange,
}) => {
  const { t, locale } = useNls()
  const { currentUser } = useAuth()
  const isMinorSelf = Boolean(learner?.isMinor && learner?.relation === 'SELF')

  const [activeSubTab, setActiveSubTab] = useState<FamilyClassSubTab>(() => {
    if (initialPtmDayId) return 'meetings'
    if (initialEventId) return 'events'
    if (initialThreadId) return 'messages'
    if (initialFeedPostId) return 'feed'
    if (initialAnnouncementId) return 'announcements'
    return initialSubTab
  })

  // Date/time helper matching MessagesTab.tsx:95-103
  const formatTime = useCallback((timestamp: unknown): string => {
    return formatEduDateTime(timestamp)
  }, [])

  // ---------------------------------------------------------------------------
  // ANNOUNCEMENTS
  // ---------------------------------------------------------------------------
  const [announcements, setAnnouncements] = useState<FamilyAnnouncementItem[]>([])
  const [announcementsCursor, setAnnouncementsCursor] = useState<string | null>(null)
  const [loadingAnnouncements, setLoadingAnnouncements] = useState<boolean>(false)
  const [announcementsError, setAnnouncementsError] = useState<string | null>(null)
  const readAnnouncementsSet = useRef<Set<string>>(new Set())

  const loadAnnouncements = useCallback(
    async (cursor?: string) => {
      setLoadingAnnouncements(true)
      setAnnouncementsError(null)
      try {
        const res = await listFamilyAnnouncements({
          workspaceId,
          learnerId,
          cursor,
        })
        const items = res?.announcements || []
        setAnnouncements((prev) => (cursor ? [...prev, ...items] : items))
        setAnnouncementsCursor(res?.nextCursor ?? null)

        // Mark read on show
        for (const item of items) {
          if (!item.readByMe && !readAnnouncementsSet.current.has(item.id)) {
            readAnnouncementsSet.current.add(item.id)
            markEduAnnouncementRead({
              workspaceId,
              announcementId: item.id,
              learnerId,
            }).catch(() => {})
          }
        }
      } catch (err) {
        setAnnouncementsError(mapEduServerError(err, t))
      } finally {
        setLoadingAnnouncements(false)
      }
    },
    [workspaceId, learnerId, t],
  )

  useEffect(() => {
    let cancelled = false
    void Promise.resolve().then(() => {
      if (!cancelled) {
        readAnnouncementsSet.current.clear()
        void loadAnnouncements()
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadAnnouncements])

  const handleToggleAnnouncementReaction = async (announcementId: string, emoji: string) => {
    if (isMinorSelf) return
    try {
      const res = await reactEduAnnouncement({
        workspaceId,
        announcementId,
        learnerId,
        emoji,
      })
      setAnnouncements((prev) =>
        prev.map((a) => {
          if (a.id !== announcementId) return a
          const currentMy = a.myReactions || []
          const nextMy = res.reacted
            ? Array.from(new Set([...currentMy, emoji]))
            : currentMy.filter((e) => e !== emoji)
          return {
            ...a,
            reactionCounts: res.reactionCounts,
            myReactions: nextMy,
          }
        }),
      )
    } catch (err) {
      setAnnouncementsError(mapEduServerError(err, t))
    }
  }

  // ---------------------------------------------------------------------------
  // FEED
  // ---------------------------------------------------------------------------
  const [feedPosts, setFeedPosts] = useState<FamilyFeedItem[]>([])
  const [feedCursor, setFeedCursor] = useState<string | null>(null)
  const [loadingFeed, setLoadingFeed] = useState<boolean>(false)
  const [feedError, setFeedError] = useState<string | null>(null)
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(null)
  const seenPostsSessionSet = useRef<Set<string>>(new Set())

  const loadFeed = useCallback(
    async (cursor?: string) => {
      setLoadingFeed(true)
      setFeedError(null)
      try {
        const res = await listFamilyFeed({
          workspaceId,
          learnerId,
          cursor,
        })
        const posts = res?.posts || []
        setFeedPosts((prev) => (cursor ? [...prev, ...posts] : posts))
        setFeedCursor(res?.nextCursor ?? null)

        // Mark seen once per post per session
        for (const post of posts) {
          if (!seenPostsSessionSet.current.has(post.id)) {
            seenPostsSessionSet.current.add(post.id)
            markEduFeedPostSeen({
              workspaceId,
              postId: post.id,
              learnerId,
            }).catch(() => {})
          }
        }
      } catch (err) {
        setFeedError(mapEduServerError(err, t))
      } finally {
        setLoadingFeed(false)
      }
    },
    [workspaceId, learnerId, t],
  )

  useEffect(() => {
    let cancelled = false
    void Promise.resolve().then(() => {
      if (!cancelled) {
        void loadFeed()
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadFeed])

  const handleToggleFeedReaction = async (postId: string, emoji: string) => {
    if (isMinorSelf) return
    try {
      const res = await reactEduFeedPost({
        workspaceId,
        postId,
        learnerId,
        emoji,
      })
      setFeedPosts((prev) =>
        prev.map((p) => {
          if (p.id !== postId) return p
          const currentMy = p.myReactions || []
          const nextMy = res.reacted
            ? Array.from(new Set([...currentMy, emoji]))
            : currentMy.filter((e) => e !== emoji)
          return {
            ...p,
            reactionCounts: res.reactionCounts,
            myReactions: nextMy,
          }
        }),
      )
    } catch (err) {
      setFeedError(mapEduServerError(err, t))
    }
  }

  // ---------------------------------------------------------------------------
  // MESSAGES
  // ---------------------------------------------------------------------------
  const [isMessagingOff, setIsMessagingOff] = useState<boolean>(false)
  const [threads, setThreads] = useState<EduThread[]>([])
  const [loadingThreads, setLoadingThreads] = useState<boolean>(false)
  const [threadsError, setThreadsError] = useState<string | null>(null)

  const [activeThreadId, setActiveThreadId] = useState<string | null>(initialThreadId)
  const [messages, setMessages] = useState<EduThreadMessage[]>([])
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null)
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)

  const [isNewMessageOpen, setIsNewMessageOpen] = useState<boolean>(false)
  const [messageableTeachers, setMessageableTeachers] = useState<MessageableTeacher[]>([])
  const [loadingTeachers, setLoadingTeachers] = useState<boolean>(false)

  const [composerText, setComposerText] = useState<string>('')
  const [composerFiles, setComposerFiles] = useState<File[]>([])
  const [sendingMessage, setSendingMessage] = useState<boolean>(false)
  const [heldNotice, setHeldNotice] = useState<string | null>(null)
  const sendRequestIdRef = useRef<string>(generateEduRequestId('msg'))

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState<string>('')
  const [savingEdit, setSavingEdit] = useState<boolean>(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // First-open Media Consent Prompt (EQ200)
  const [showConsentPrompt, setShowConsentPrompt] = useState<boolean>(false)
  const [selectedPromptConsent, setSelectedPromptConsent] = useState<MediaConsentLevel | null>(null)
  const [savingConsentPrompt, setSavingConsentPrompt] = useState<boolean>(false)
  const [consentPromptError, setConsentPromptError] = useState<string | null>(null)
  const checkedConsentRef = useRef<string | null>(null)

  useEffect(() => {
    if (activeSubTab !== 'feed' || isMinorSelf || !workspaceId || !learnerId) {
      return
    }
    const checkKey = `${workspaceId}_${learnerId}`
    if (checkedConsentRef.current === checkKey) {
      return
    }

    const decideLaterKey = `edu_media_consent_decide_later_${workspaceId}_${learnerId}`
    try {
      if (typeof window !== 'undefined' && window.localStorage?.getItem(decideLaterKey) === 'true') {
        checkedConsentRef.current = checkKey
        return
      }
    } catch {
      // ignore localStorage errors
    }

    let active = true
    getEduMediaConsent({ workspaceId, learnerId })
      .then((res) => {
        if (!active) return
        checkedConsentRef.current = checkKey
        if (!res.consent || res.consent.setBy === null) {
          setSelectedPromptConsent(null)
          setShowConsentPrompt(true)
        }
      })
      .catch(() => {
        // ignore errors on initial consent prompt check
      })

    return () => {
      active = false
    }
  }, [activeSubTab, isMinorSelf, workspaceId, learnerId])

  const handleDecideLater = () => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(
          `edu_media_consent_decide_later_${workspaceId}_${learnerId}`,
          'true',
        )
      }
    } catch {
      // ignore
    }
    setShowConsentPrompt(false)
  }

  const handleSaveConsentPrompt = async () => {
    if (!selectedPromptConsent) return
    setSavingConsentPrompt(true)
    setConsentPromptError(null)
    try {
      await setEduMediaConsent({
        workspaceId,
        learnerId,
        level: selectedPromptConsent,
      })
      setShowConsentPrompt(false)
    } catch (err) {
      setConsentPromptError(mapEduServerError(err, t))
    } finally {
      setSavingConsentPrompt(false)
    }
  }

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true)
    setThreadsError(null)
    try {
      const res = await listEduThreads({ workspaceId, learnerId })
      setThreads(res.threads || [])
      if (res.messagingEnabled === false) {
        setIsMessagingOff(true)
      } else {
        setIsMessagingOff(false)
      }
    } catch (err: unknown) {
      if (eduServerErrorCode(err) === 'MESSAGING_OFF') {
        setIsMessagingOff(true)
      } else {
        setThreadsError(mapEduServerError(err, t))
      }
    } finally {
      setLoadingThreads(false)
    }
  }, [workspaceId, learnerId, t])

  useEffect(() => {
    let cancelled = false
    void Promise.resolve().then(() => {
      if (!cancelled) {
        void loadThreads()
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadThreads])

  // Load messages when activeThreadId changes
  const loadMessages = useCallback(
    async (threadId: string, cursor?: string) => {
      setLoadingMessages(true)
      setMessagesError(null)
      try {
        const res = await listEduThreadMessages({
          workspaceId,
          threadId,
          cursor,
        })
        const fetchedMessages = res.messages || []
        // Sort oldest first for thread conversation
        const sorted = [...fetchedMessages].sort((a, b) => {
          const aMs = a.createdAt ? new Date(a.createdAt as string).getTime() : 0
          const bMs = b.createdAt ? new Date(b.createdAt as string).getTime() : 0
          return aMs - bMs
        })
        setMessages((prev) => (cursor ? [...sorted, ...prev] : sorted))
        setMessagesCursor(res.nextCursor)

        // Mark thread read
        markEduThreadRead({ workspaceId, threadId })
          .then(() => {
            setThreads((prev) =>
              prev.map((t) => (t.id === threadId ? { ...t, unreadCountFamily: 0 } : t)),
            )
          })
          .catch(() => {})
      } catch (err) {
        setMessagesError(mapEduServerError(err, t))
      } finally {
        setLoadingMessages(false)
      }
    },
    [workspaceId, t],
  )

  useEffect(() => {
    if (activeThreadId) {
      void Promise.resolve().then(() => {
        void loadMessages(activeThreadId)
      })
    }
  }, [activeThreadId, loadMessages])

  // Load messageable teachers
  const handleOpenNewMessage = async () => {
    if (isMinorSelf) return
    setIsNewMessageOpen(true)
    setLoadingTeachers(true)
    try {
      const res = await listEduMessageableTeachers({ workspaceId, learnerId })
      if (res.messagingEnabled === false) {
        setIsMessagingOff(true)
        setIsNewMessageOpen(false)
        return
      }
      setMessageableTeachers(res.teachers || [])
    } catch (err: unknown) {
      if (eduServerErrorCode(err) === 'MESSAGING_OFF') {
        setIsMessagingOff(true)
        setIsNewMessageOpen(false)
      } else {
        setThreadsError(mapEduServerError(err, t))
      }
    } finally {
      setLoadingTeachers(false)
    }
  }

  const handleStartThreadWithTeacher = async (teacher: MessageableTeacher) => {
    try {
      const res = await openEduThread({
        workspaceId,
        learnerId,
        staffUid: teacher.uid,
      })
      setIsNewMessageOpen(false)
      setActiveThreadId(res.thread.id)
      setThreads((prev) => {
        if (prev.some((t) => t.id === res.thread.id)) return prev
        return [res.thread, ...prev]
      })
    } catch (err: unknown) {
      if (eduServerErrorCode(err) === 'MESSAGING_OFF') {
        setIsMessagingOff(true)
        setIsNewMessageOpen(false)
      } else {
        setThreadsError(mapEduServerError(err, t))
      }
    }
  }

  const handleSendMessage = async () => {
    if (!activeThreadId || isMinorSelf) return
    const text = composerText.trim()
    if (!text && composerFiles.length === 0) return

    setSendingMessage(true)
    setMessagesError(null)
    setHeldNotice(null)

    try {
      let fileIds: string[] = []
      if (composerFiles.length > 0) {
        fileIds = await uploadThreadMessageFiles({
          workspaceId,
          requestId: sendRequestIdRef.current,
          files: composerFiles,
        })
      }

      const res = await sendEduThreadMessage({
        workspaceId,
        threadId: activeThreadId,
        text,
        fileIds,
        requestId: sendRequestIdRef.current,
      })

      setMessages((prev) => [...prev, res.message])
      setComposerText('')
      setComposerFiles([])
      // Generate next requestId only on success
      sendRequestIdRef.current = generateEduRequestId('msg')

      if (res.heldUntil) {
        const timeFormatted = formatTime(res.heldUntil)
        setHeldNotice(
          t('NLSFamilyMessagesDeliveredNotice', {
            time: timeFormatted,
          }),
        )
      }
    } catch (err: unknown) {
      setMessagesError(mapEduServerError(err, t))
      // On failure, sendRequestIdRef.current is intentionally kept for retry
    } finally {
      setSendingMessage(false)
    }
  }

  const handleSaveEdit = async (messageId: string) => {
    const text = editingText.trim()
    if (!text) return
    setSavingEdit(true)
    try {
      const res = await editEduThreadMessage({
        workspaceId,
        messageId,
        text,
      })
      setMessages((prev) => prev.map((m) => (m.id === messageId ? res.message : m)))
      setEditingMessageId(null)
      setEditingText('')
    } catch (err) {
      setMessagesError(mapEduServerError(err, t))
    } finally {
      setSavingEdit(false)
    }
  }

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteEduThreadMessage({ workspaceId, messageId })
      setMessages((prev) => prev.filter((m) => m.id !== messageId))
      setDeleteConfirmId(null)
    } catch (err) {
      setMessagesError(mapEduServerError(err, t))
    }
  }

  // ---------------------------------------------------------------------------
  // EVENTS
  // ---------------------------------------------------------------------------
  const [eventsMode, setEventsMode] = useState<'upcoming' | 'past'>('upcoming')
  const [events, setEvents] = useState<FamilyEduEventItem[]>([])
  const [eventsCursor, setEventsCursor] = useState<string | null>(null)
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<FamilyEduEventItem | null>(null)

  const loadEvents = useCallback(
    async (mode: 'upcoming' | 'past', cursor?: string) => {
      setLoadingEvents(true)
      setEventsError(null)
      try {
        const now = Date.now()
        let from: string
        let to: string
        if (mode === 'upcoming') {
          from = new Date(now - 12 * 60 * 60 * 1000).toISOString()
          to = new Date(now + 92 * 24 * 60 * 60 * 1000).toISOString()
        } else {
          from = new Date(now - 92 * 24 * 60 * 60 * 1000).toISOString()
          to = new Date(now).toISOString()
        }

        const res = await listFamilyEduEvents({
          workspaceId,
          learnerId,
          from,
          to,
          cursor,
        })
        const items = res?.events || []
        setEvents((prev) => (cursor ? [...prev, ...items] : items))
        setEventsCursor(res?.nextCursor ?? null)
      } catch {
        setEventsError(t('NLSFamilyEventsLoadError'))
      } finally {
        setLoadingEvents(false)
      }
    },
    [workspaceId, learnerId, t],
  )

  useEffect(() => {
    let active = true
    if (activeSubTab === 'events' && workspaceId && learnerId) {
      void Promise.resolve().then(() => {
        if (active) {
          void loadEvents(eventsMode)
        }
      })
    }
    return () => {
      active = false
    }
  }, [activeSubTab, eventsMode, loadEvents, workspaceId, learnerId])

  useEffect(() => {
    let active = true
    if (initialEventId && workspaceId && learnerId) {
      void Promise.resolve().then(() => {
        if (active) {
          setActiveSubTab('events')
        }
      })
      void getFamilyEduEvent({
        workspaceId,
        learnerId,
        eventId: initialEventId,
      })
        .then((res) => {
          if (active && res?.event) {
            setSelectedEvent(res.event)
          }
        })
        .catch(() => {})
    }
    return () => {
      active = false
    }
  }, [initialEventId, workspaceId, learnerId])

  const handleRsvpChange = (eventId: string, response: 'GOING' | 'NOT_GOING' | 'MAYBE') => {
    setEvents((prev) =>
      prev.map((e) => (e.id === eventId ? { ...e, myRsvp: response } : e)),
    )
    if (selectedEvent && selectedEvent.id === eventId) {
      setSelectedEvent((prev) => (prev ? { ...prev, myRsvp: response } : null))
    }
  }

  const rsvpLabel = (r: 'GOING' | 'NOT_GOING' | 'MAYBE' | null | undefined) => {
    switch (r) {
      case 'GOING':
        return t('NLSFamilyEventRsvpGoing')
      case 'NOT_GOING':
        return t('NLSFamilyEventRsvpNotGoing')
      case 'MAYBE':
        return t('NLSFamilyEventRsvpMaybe')
      default:
        return t('NLSFamilyEventRsvpNone')
    }
  }

  // ---------------------------------------------------------------------------
  // MEETINGS (PTM)
  // ---------------------------------------------------------------------------
  const [ptmDays, setPtmDays] = useState<EduPtmDayItem[]>([])
  const [loadingPtmDays, setLoadingPtmDays] = useState<boolean>(false)
  const [ptmDaysError, setPtmDaysError] = useState<string | null>(null)
  const [selectedPtmDay, setSelectedPtmDay] = useState<EduPtmDayItem | null>(null)
  const [otherLearnerBookings, setOtherLearnerBookings] = useState<OtherChildBooking[]>([])
  const [ptmReloadSeq, setPtmReloadSeq] = useState<number>(0)

  const loadPtmDays = useCallback(async () => {
    if (!workspaceId || !learnerId) return
    setLoadingPtmDays(true)
    setPtmDaysError(null)
    try {
      const res = await listFamilyEduPtmDays({ workspaceId, learnerId })
      const days = res?.days || []
      setPtmDays(days)
      if (initialPtmDayId) {
        const matching = days.find((d) => d.id === initialPtmDayId)
        if (matching) {
          setSelectedPtmDay(matching)
        }
      }
    } catch {
      setPtmDaysError(t('NLSFamilyEventsLoadError'))
    } finally {
      setLoadingPtmDays(false)
    }
  }, [workspaceId, learnerId, initialPtmDayId, t])

  useEffect(() => {
    let active = true
    if (activeSubTab === 'meetings' && workspaceId && learnerId) {
      void Promise.resolve().then(() => {
        if (active) void loadPtmDays()
      })
    }
    return () => {
      active = false
    }
  }, [activeSubTab, workspaceId, learnerId, loadPtmDays, ptmReloadSeq])

  useEffect(() => {
    let active = true
    if (!selectedPtmDay || !otherLearners || otherLearners.length === 0) {
      void Promise.resolve().then(() => {
        if (active) setOtherLearnerBookings([])
      })
      return
    }
    const dayId = selectedPtmDay.id
    Promise.all(
      otherLearners.map(async (ol) => {
        try {
          const res = await listFamilyEduPtmTeachers({
            workspaceId: ol.workspaceId,
            learnerId: ol.learnerId,
            dayId,
          })
          const bookings: OtherChildBooking[] = []
          for (const tc of res?.teachers || []) {
            if (tc.booking && tc.booking.status === 'BOOKED') {
              bookings.push({
                learnerId: ol.learnerId,
                slotStartMs: new Date(tc.booking.slotStart).getTime(),
                slotEndMs: new Date(tc.booking.slotEnd).getTime(),
              })
            }
          }
          return bookings
        } catch {
          return []
        }
      }),
    ).then((results) => {
      if (!active) return
      setOtherLearnerBookings(results.flat())
    })
    return () => {
      active = false
    }
  }, [selectedPtmDay, otherLearners])

  // ---------------------------------------------------------------------------
  // Unread badge calculations
  // ---------------------------------------------------------------------------
  const unreadAnnouncementsCount = useMemo(
    () => announcements.filter((a) => !a.readByMe).length,
    [announcements],
  )
  const newFeedPostsCount = useMemo(
    () => feedPosts.filter((p) => !p.seenByMe).length,
    [feedPosts],
  )
  const unreadMessagesCount = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unreadCountFamily || 0), 0),
    [threads],
  )

  useEffect(() => {
    onCountsChange?.({
      unreadAnnouncements: unreadAnnouncementsCount,
      newFeedPosts: newFeedPostsCount,
      unreadMessages: unreadMessagesCount,
    })
  }, [unreadAnnouncementsCount, newFeedPostsCount, unreadMessagesCount, onCountsChange])

  // Check 24 hour edit/delete eligibility
  const canEditOrDelete = useCallback(
    (msg: EduThreadMessage): boolean => {
      if (isMinorSelf) return false
      const isFamilyAuthor =
        msg.senderRole === 'FAMILY' || (currentUser?.uid && msg.senderUid === currentUser.uid)
      if (!isFamilyAuthor) return false
      const createdMs = msg.createdAt ? new Date(msg.createdAt as string).getTime() : 0
      const nowMs = Date.now()
      return nowMs - createdMs <= 24 * 60 * 60 * 1000
    },
    [isMinorSelf, currentUser],
  )

  if (frozen) {
    return (
      <div className="familyClassTab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyClassTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  return (
    <div className="familyClassTab" data-testid="family-class-tab">
      <div className="familyClassTab__segmentedNav">
        <SegmentedControl
          ariaLabel={t('NLSFamilyTabClass')}
          value={activeSubTab}
          onChange={(val) => {
            setActiveSubTab(val as FamilyClassSubTab)
            setHeldNotice(null)
          }}
          options={[
            {
              value: 'announcements',
              label:
                unreadAnnouncementsCount > 0
                  ? `${t('NLSFamilySubTabAnnouncements')} (${unreadAnnouncementsCount})`
                  : t('NLSFamilySubTabAnnouncements'),
            },
            {
              value: 'feed',
              label:
                newFeedPostsCount > 0
                  ? `${t('NLSFamilySubTabFeed')} (${newFeedPostsCount})`
                  : t('NLSFamilySubTabFeed'),
            },
            {
              value: 'messages',
              label:
                unreadMessagesCount > 0
                  ? `${t('NLSFamilySubTabMessages')} (${unreadMessagesCount})`
                  : t('NLSFamilySubTabMessages'),
            },
            {
              value: 'events',
              label: t('NLSFamilySubTabEvents'),
            },
            {
              value: 'meetings',
              label: t('NLSFamilySubTabMeetings'),
            },
          ]}
        />
      </div>

      {/* ANNOUNCEMENTS SUB-TAB */}
      {activeSubTab === 'announcements' && (
        <section
          className="familyClassTab__subtabContent"
          aria-label={t('NLSFamilySubTabAnnouncements')}
          data-testid="family-announcements-section"
        >
          {announcementsError && (
            <div className="familyClassTab__error" role="alert">
              {announcementsError}
            </div>
          )}

          {loadingAnnouncements && announcements.length === 0 ? (
            <div className="familyClassTab__loader">
              <MiniLoader />
            </div>
          ) : announcements.length === 0 ? (
            <FamilyCard>
              <div className="familyClassTab__emptyState">
                <div className="familyClassTab__emptyTitle">
                  {t('NLSFamilyAnnouncementsEmptyTitle')}
                </div>
                <div className="familyClassTab__emptyBody">
                  {t('NLSFamilyAnnouncementsEmptyBody')}
                </div>
              </div>
            </FamilyCard>
          ) : (
            <div className="familyClassTab__cardList">
              {announcements.map((ann) => (
                <div
                  key={ann.id}
                  className={`familyClassTab__card ${
                    initialAnnouncementId === ann.id ? 'familyClassTab__card--highlight' : ''
                  }`}
                  data-testid={`announcement-card-${ann.id}`}
                >
                  <div className="familyClassTab__cardHeader">
                    <h3 className="familyClassTab__cardTitle">{ann.title}</h3>
                    <div className="familyClassTab__cardMeta">
                      <span className="familyClassTab__cardAuthor">
                        {ann.createdByName || ann.createdBy}
                      </span>
                      {Boolean(ann.createdAt) && (
                        <span className="familyClassTab__cardTime">{formatTime(ann.createdAt)}</span>
                      )}
                      {Boolean(ann.editedAt) && (
                        <span className="familyClassTab__editedTag">
                          {t('NLSFamilyAnnouncementEdited')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="familyClassTab__cardBody">{ann.body}</div>

                  {ann.attachments && ann.attachments.length > 0 && (
                    <div className="familyClassTab__attachments">
                      <span className="familyClassTab__attachmentsLabel">
                        {t('NLSFamilyAnnouncementAttachments')}:
                      </span>
                      <ul className="familyClassTab__attachmentsList">
                        {ann.attachments.map((att, i) => (
                          <li key={att.fileId || att.id || i}>
                            <a
                              href={att.downloadUrl || '#'}
                              download={att.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="familyClassTab__attachmentLink"
                            >
                              📎 {att.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="familyClassTab__reactionsRow">
                    <ReactionBar
                      counts={ann.reactionCounts || {}}
                      mine={ann.myReactions || []}
                      readOnly={isMinorSelf}
                      onToggle={(emoji) => handleToggleAnnouncementReaction(ann.id, emoji)}
                      testId={`reaction-bar-ann-${ann.id}`}
                    />
                    {isMinorSelf && (
                      <div className="familyClassTab__minorNote" role="note">
                        {t('NLSFamilyMinorReadOnlyNote')}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {announcementsCursor && (
                <div className="familyClassTab__loadMore">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={loadingAnnouncements}
                    onClick={() => loadAnnouncements(announcementsCursor)}
                  >
                    {t('NLSFamilyAnnouncementsLoadMore')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* FEED SUB-TAB */}
      {activeSubTab === 'feed' && (
        <section
          className="familyClassTab__subtabContent"
          aria-label={t('NLSFamilySubTabFeed')}
          data-testid="family-feed-section"
        >
          {feedError && (
            <div className="familyClassTab__error" role="alert">
              {feedError}
            </div>
          )}

          {loadingFeed && feedPosts.length === 0 ? (
            <div className="familyClassTab__loader">
              <MiniLoader />
            </div>
          ) : feedPosts.length === 0 ? (
            <FamilyCard>
              <div className="familyClassTab__emptyState">
                <div className="familyClassTab__emptyTitle">{t('NLSFamilyFeedEmptyTitle')}</div>
                <div className="familyClassTab__emptyBody">{t('NLSFamilyFeedEmptyBody')}</div>
              </div>
            </FamilyCard>
          ) : (
            <div className="familyClassTab__cardList">
              {feedPosts.map((post) => {
                const imageAttachments = (post.attachments || []).filter(
                  (a) =>
                    a.contentType?.startsWith('image/') ||
                    /\.(jpe?g|png|gif|webp)$/i.test(a.name || ''),
                )
                const videoAttachments = (post.attachments || []).filter(
                  (a) =>
                    a.contentType?.startsWith('video/') ||
                    /\.(mp4|webm|mov)$/i.test(a.name || ''),
                )

                return (
                  <div
                    key={post.id}
                    className={`familyClassTab__card ${
                      initialFeedPostId === post.id ? 'familyClassTab__card--highlight' : ''
                    }`}
                    data-testid={`feed-post-card-${post.id}`}
                  >
                    <div className="familyClassTab__cardHeader">
                      <div className="familyClassTab__cardMeta">
                        <span className="familyClassTab__cardAuthor">
                          {post.createdByName || post.createdBy}
                        </span>
                        {Boolean(post.createdAt) && (
                          <span className="familyClassTab__cardTime">
                            {formatTime(post.createdAt)}
                          </span>
                        )}
                        {Boolean(post.editedAt) && (
                          <span className="familyClassTab__editedTag">
                            {t('NLSFamilyFeedEdited')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="familyClassTab__cardBody">{post.body}</div>

                    {/* Media grid */}
                    {imageAttachments.length > 0 && (
                      <div className="familyClassTab__mediaGrid">
                        {imageAttachments.map((img, i) => (
                          <button
                            key={img.fileId || img.id || i}
                            type="button"
                            className="familyClassTab__imageThumbBtn"
                            onClick={() => setSelectedPhotoUrl(img.downloadUrl || '')}
                            aria-label={t('NLSFamilyFeedViewImage')}
                          >
                            <img
                              src={img.downloadUrl || ''}
                              alt={img.name}
                              className="familyClassTab__imageThumb"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    )}

                    {videoAttachments.length > 0 && (
                      <div className="familyClassTab__videoLinks">
                        {videoAttachments.map((vid, i) => (
                          <a
                            key={vid.fileId || vid.id || i}
                            href={vid.downloadUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="familyClassTab__videoLink"
                          >
                            ▶ {t('NLSFamilyFeedVideoLink')} ({vid.name})
                          </a>
                        ))}
                      </div>
                    )}

                    <div className="familyClassTab__reactionsRow">
                      <ReactionBar
                        counts={post.reactionCounts || {}}
                        mine={post.myReactions || []}
                        readOnly={isMinorSelf}
                        onToggle={(emoji) => handleToggleFeedReaction(post.id, emoji)}
                        testId={`reaction-bar-feed-${post.id}`}
                      />
                      {isMinorSelf && (
                        <div className="familyClassTab__minorNote" role="note">
                          {t('NLSFamilyMinorReadOnlyNote')}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {feedCursor && (
                <div className="familyClassTab__loadMore">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={loadingFeed}
                    onClick={() => loadFeed(feedCursor)}
                  >
                    {t('NLSFamilyFeedLoadMore')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Photo viewer AppDialog */}
          <AppDialog
            open={Boolean(selectedPhotoUrl)}
            onDismiss={() => setSelectedPhotoUrl(null)}
            title={t('NLSFamilyFeedViewImage')}
            size="lg"
            actions={[
              {
                label: t('NLSFamilyFeedDialogClose'),
                variant: 'secondary',
                onClick: () => setSelectedPhotoUrl(null),
              },
            ]}
          >
            {selectedPhotoUrl ? (
              <div className="familyClassTab__modalPhotoWrapper">
                <img
                  src={selectedPhotoUrl}
                  alt={t('NLSFamilyFeedViewImage')}
                  className="familyClassTab__modalPhoto"
                />
              </div>
            ) : null}
          </AppDialog>
        </section>
      )}

      {/* MESSAGES SUB-TAB */}
      {activeSubTab === 'messages' && (
        <section
          className="familyClassTab__subtabContent"
          aria-label={t('NLSFamilySubTabMessages')}
          data-testid="family-messages-section"
        >
          {isMessagingOff ? (
            <FamilyCard>
              <div className="familyClassTab__emptyState" data-testid="messaging-disabled-notice">
                <div className="familyClassTab__emptyTitle">
                  {t('NLSFamilyMessagesDisabledTitle')}
                </div>
                <div className="familyClassTab__emptyBody">
                  {t('NLSFamilyMessagesDisabledBody')}
                </div>
              </div>
            </FamilyCard>
          ) : activeThreadId ? (
            /* CONVERSATION VIEW */
            <div className="familyClassTab__threadView" data-testid="thread-conversation-view">
              <div className="familyClassTab__threadViewHeader">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setActiveThreadId(null)
                    setHeldNotice(null)
                    setEditingMessageId(null)
                  }}
                >
                  ← {t('NLSFamilyMessagesBackToThreads')}
                </Button>
              </div>

              {messagesError && (
                <div className="familyClassTab__error" role="alert">
                  {messagesError}
                </div>
              )}

              {heldNotice && (
                <div
                  className="familyClassTab__heldNotice"
                  role="status"
                  data-testid="held-notice-banner"
                >
                  {heldNotice}
                </div>
              )}

              {loadingMessages && messages.length === 0 ? (
                <div className="familyClassTab__loader">
                  <MiniLoader />
                </div>
              ) : (
                <div className="familyClassTab__messagesList">
                  {messagesCursor && (
                    <div className="familyClassTab__loadMore">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={loadingMessages}
                        onClick={() => loadMessages(activeThreadId, messagesCursor)}
                      >
                        {t('NLSFamilyMessagesLoadMore')}
                      </Button>
                    </div>
                  )}

                  {messages.map((msg) => {
                    const isFamily = msg.senderRole === 'FAMILY'
                    const editable = canEditOrDelete(msg)
                    const isEditing = editingMessageId === msg.id

                    return (
                      <div
                        key={msg.id}
                        className={`familyClassTab__messageItem ${
                          isFamily
                            ? 'familyClassTab__messageItem--family'
                            : 'familyClassTab__messageItem--staff'
                        }`}
                        data-testid={`thread-message-${msg.id}`}
                      >
                        <div className="familyClassTab__messageBubble">
                          {isEditing ? (
                            <div className="familyClassTab__editBox">
                              <textarea
                                className="familyClassTab__editTextarea"
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                rows={2}
                              />
                              <div className="familyClassTab__editActions">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={savingEdit || !editingText.trim()}
                                  onClick={() => handleSaveEdit(msg.id)}
                                >
                                  {t('NLSFamilyMessagesSaveEdit')}
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingMessageId(null)
                                    setEditingText('')
                                  }}
                                >
                                  {t('NLSFamilyMessagesCancelEdit')}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="familyClassTab__messageText">{msg.text}</div>
                              {msg.attachments && msg.attachments.length > 0 && (
                                <ul className="familyClassTab__messageAttachments">
                                  {msg.attachments.map((att, i) => (
                                    <li key={att.fileId || att.id || i}>
                                      <a
                                        href={att.downloadUrl || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="familyClassTab__attachmentLink"
                                      >
                                        📎 {att.name}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </>
                          )}

                          <div className="familyClassTab__messageMeta">
                            {Boolean(msg.createdAt) && (
                              <span className="familyClassTab__messageTime">
                                {formatTime(msg.createdAt)}
                              </span>
                            )}
                            {Boolean(msg.editedAt) && (
                              <span className="familyClassTab__editedTag">
                                {t('NLSFamilyAnnouncementEdited')}
                              </span>
                            )}
                          </div>
                        </div>

                        {editable && !isEditing && (
                          <div className="familyClassTab__messageActions">
                            <button
                              type="button"
                              className="familyClassTab__actionLink"
                              onClick={() => {
                                setEditingMessageId(msg.id)
                                setEditingText(msg.text)
                              }}
                            >
                              {t('NLSFamilyMessagesEdit')}
                            </button>
                            <button
                              type="button"
                              className="familyClassTab__actionLink familyClassTab__actionLink--danger"
                              onClick={() => setDeleteConfirmId(msg.id)}
                            >
                              {t('NLSFamilyMessagesDelete')}
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Composer */}
              {isMinorSelf ? (
                <div className="familyClassTab__minorNotice" role="note">
                  {t('NLSFamilyMessagesMinorNotice')}
                </div>
              ) : (
                <div className="familyClassTab__composer" data-testid="message-composer">
                  {(() => {
                    const activeThread = threads.find((t) => t.id === activeThreadId)
                    const activeTeacher = messageableTeachers.find((tc) => tc.uid === activeThread?.staffUid)
                    if (activeTeacher && !activeTeacher.isInsideHours && activeTeacher.nextWindowStart) {
                      return (
                        <div
                          className="familyClassTab__outsideHoursNotice"
                          data-testid="composer-outside-hours-notice"
                        >
                          {t('NLSFamilyMessagesOutsideOfficeHours', {
                            time: formatTime(activeTeacher.nextWindowStart),
                          })}
                        </div>
                      )
                    }
                    return null
                  })()}
                  <textarea
                    className="familyClassTab__composerTextarea"
                    placeholder={t('NLSFamilyMessagesPlaceholder')}
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    rows={2}
                  />

                  <div className="familyClassTab__composerFooter">
                    <label className="familyClassTab__fileUploadLabel">
                      <span>📎 {t('NLSFamilyMessagesAttachFiles')}</span>
                      <input
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        className="familyClassTab__hiddenFileInput"
                        onChange={(e) => {
                          if (e.target.files) {
                            const files = Array.from(e.target.files)
                            const total = composerFiles.length + files.length
                            if (total > 5) {
                              setMessagesError(t('NLSEducationFeedMaxMediaError'))
                              return
                            }
                            setComposerFiles((prev) => [...prev, ...files])
                          }
                        }}
                      />
                    </label>

                    {composerFiles.length > 0 && (
                      <div className="familyClassTab__composerFilesList">
                        {composerFiles.map((f, i) => (
                          <span key={i} className="familyClassTab__composerFileBadge">
                            {f.name}
                            <button
                              type="button"
                              className="familyClassTab__removeFileBtn"
                              onClick={() =>
                                setComposerFiles((prev) => prev.filter((_, idx) => idx !== i))
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <Button
                      variant="primary"
                      size="sm"
                      disabled={sendingMessage || (!composerText.trim() && composerFiles.length === 0)}
                      onClick={handleSendMessage}
                    >
                      {sendingMessage
                        ? t('NLSFamilyMessagesSending')
                        : t('NLSFamilyMessagesSend')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* THREADS LIST */
            <div className="familyClassTab__threadsListArea" data-testid="threads-list-area">
              <div className="familyClassTab__threadsHeader">
                {isMinorSelf ? (
                  <div className="familyClassTab__minorNotice" role="note">
                    {t('NLSFamilyMessagesMinorNotice')}
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenNewMessage}
                    data-testid="new-message-button"
                  >
                    {t('NLSFamilyMessagesNewMessage')}
                  </Button>
                )}
              </div>

              {threadsError && (
                <div className="familyClassTab__error" role="alert">
                  {threadsError}
                </div>
              )}

              {loadingThreads && threads.length === 0 ? (
                <div className="familyClassTab__loader">
                  <MiniLoader />
                </div>
              ) : threads.length === 0 ? (
                <FamilyCard>
                  <div className="familyClassTab__emptyState">
                    <div className="familyClassTab__emptyTitle">
                      {t('NLSFamilyMessagesEmptyTitle')}
                    </div>
                    <div className="familyClassTab__emptyBody">
                      {t('NLSFamilyMessagesEmptyBody')}
                    </div>
                  </div>
                </FamilyCard>
              ) : (
                <div className="familyClassTab__threadList">
                  {threads.map((thr) => (
                    <div
                      key={thr.id}
                      className="familyClassTab__threadRow"
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveThreadId(thr.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setActiveThreadId(thr.id)
                        }
                      }}
                      data-testid={`thread-row-${thr.id}`}
                    >
                      <div className="familyClassTab__threadRowMeta">
                        <div className="familyClassTab__threadTitle">
                          {t('NLSFamilySubTabMessages')} ({thr.staffUid})
                        </div>
                        {Boolean(thr.lastMessageAt) && (
                          <div className="familyClassTab__threadTime">
                            {formatTime(thr.lastMessageAt)}
                          </div>
                        )}
                      </div>

                      {thr.unreadCountFamily > 0 && (
                        <span
                          className="familyClassTab__unreadBadge"
                          aria-label={`${thr.unreadCountFamily} unread`}
                        >
                          {thr.unreadCountFamily}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* New Message Dialog */}
          <AppDialog
            open={isNewMessageOpen}
            onDismiss={() => setIsNewMessageOpen(false)}
            title={t('NLSFamilyMessagesSelectTeacher')}
            size="md"
            actions={[
              {
                label: t('NLSFamilyFeedDialogClose'),
                variant: 'secondary',
                onClick: () => setIsNewMessageOpen(false),
              },
            ]}
          >
            {loadingTeachers ? (
              <div className="familyClassTab__loader">
                <MiniLoader />
              </div>
            ) : messageableTeachers.length === 0 ? (
              <div className="familyClassTab__emptyState">
                <div className="familyClassTab__emptyBody">
                  {t('NLSEducationFeedNoLearnersFound')}
                </div>
              </div>
            ) : (
              <ul className="familyClassTab__teacherList">
                {messageableTeachers.map((tc) => {
                  const outsideNotice =
                    !tc.isInsideHours && tc.nextWindowStart
                      ? t('NLSFamilyMessagesOutsideOfficeHours', {
                          time: formatTime(tc.nextWindowStart),
                        })
                      : null

                  return (
                    <li key={tc.uid} className="familyClassTab__teacherItem">
                      <button
                        type="button"
                        className="familyClassTab__teacherBtn"
                        onClick={() => handleStartThreadWithTeacher(tc)}
                        data-testid={`teacher-btn-${tc.uid}`}
                      >
                        <div className="familyClassTab__teacherInfo">
                          <span className="familyClassTab__teacherName">{tc.displayName}</span>
                          <span className="familyClassTab__teacherRole">{tc.role}</span>
                        </div>
                        {outsideNotice && (
                          <div className="familyClassTab__outsideHoursNotice">
                            {outsideNotice}
                          </div>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </AppDialog>

          {/* Delete Message Confirmation */}
          <ConfirmActionModal
            open={Boolean(deleteConfirmId)}
            title={t('NLSFamilyMessagesDeleteConfirmTitle')}
            body={t('NLSFamilyMessagesDeleteConfirmBody')}
            confirmLabel={t('NLSFamilyMessagesDelete')}
            danger
            onConfirm={() => {
              if (deleteConfirmId) {
                void handleDeleteMessage(deleteConfirmId)
              }
            }}
            onClose={() => setDeleteConfirmId(null)}
          />
        </section>
      )}

      {/* EVENTS SUB-TAB */}
      {activeSubTab === 'events' && (
        <section
          className="familyClassTab__subtabContent"
          aria-label={t('NLSFamilySubTabEvents')}
          data-testid="family-events-section"
        >
          <div className="familyClassTab__eventsFilterRow">
            <Button
              variant={eventsMode === 'upcoming' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setEventsMode('upcoming')
                setEvents([])
                setEventsCursor(null)
              }}
            >
              {t('NLSFamilyEventsUpcoming')}
            </Button>
            <Button
              variant={eventsMode === 'past' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setEventsMode('past')
                setEvents([])
                setEventsCursor(null)
              }}
            >
              {t('NLSFamilyEventsPast')}
            </Button>
          </div>

          {eventsError && (
            <div className="familyClassTab__error" role="alert">
              {eventsError}
            </div>
          )}

          {loadingEvents && events.length === 0 && (
            <div className="familyClassTab__loader" aria-label={t('NLSFamilyEventsLoading')}>
              <MiniLoader />
            </div>
          )}

          {!loadingEvents && events.length === 0 && !eventsError && (
            <div className="familyClassTab__emptyState">
              <div className="familyClassTab__emptyTitle">
                {eventsMode === 'upcoming'
                  ? t('NLSFamilyEventsEmptyUpcoming')
                  : t('NLSFamilyEventsEmptyPast')}
              </div>
            </div>
          )}

          {events.length > 0 && (
            <div className="familyClassTab__cardList">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="familyClassTab__card familyClassTab__eventCard"
                  onClick={() => setSelectedEvent(ev)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelectedEvent(ev)
                    }
                  }}
                  data-testid={`event-card-${ev.id}`}
                >
                  <div className="familyClassTab__cardHeader">
                    <div className="familyClassTab__eventRowTop">
                      <span className="familyClassTab__eventTime">
                        {ev.allDay
                          ? formatEduDate(ev.startAt, locale, ev.timezone)
                          : formatEduDateTime(ev.startAt, locale, ev.timezone)}
                      </span>
                      {ev.status === 'CANCELLED' && (
                        <StatusChip tone="danger" size="sm">
                          {t('NLSFamilyEventCancelled')}
                        </StatusChip>
                      )}
                    </div>
                    <h4 className="familyClassTab__cardTitle">{ev.title}</h4>
                    {ev.status === 'CANCELLED' && ev.cancelReason && (
                      <div className="familyClassTab__cancelReason">
                        {t('NLSFamilyEventCancelReason', { reason: ev.cancelReason })}
                      </div>
                    )}
                  </div>

                  {(ev.location || ev.onlineUrl) && (
                    <div className="familyClassTab__eventMeta">
                      {ev.location && (
                        <span className="familyClassTab__eventPlace">📍 {ev.location}</span>
                      )}
                      {ev.onlineUrl && (
                        <span className="familyClassTab__eventPlace">🔗 {ev.onlineUrl}</span>
                      )}
                    </div>
                  )}

                  <div className="familyClassTab__eventFooter">
                    <span className="familyClassTab__eventRsvpStatus">
                      {t('NLSFamilyEventChildAnswer')}: <strong>{rsvpLabel(ev.myRsvp)}</strong>
                    </span>
                  </div>
                </div>
              ))}

              {eventsCursor && (
                <div className="familyClassTab__loadMore">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={loadingEvents}
                    onClick={() => void loadEvents(eventsMode, eventsCursor)}
                  >
                    {t('NLSFamilyEventLoadMore')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {selectedEvent && (
            <FamilyEventDetailDialog
              isOpen={Boolean(selectedEvent)}
              onClose={() => setSelectedEvent(null)}
              workspaceId={workspaceId}
              learnerId={learnerId}
              learner={learner}
              event={selectedEvent}
              onRsvpChange={handleRsvpChange}
            />
          )}
        </section>
      )}

      {/* MEETINGS SUB-TAB */}
      {activeSubTab === 'meetings' && (
        <section
          className="familyClassTab__subtabContent"
          aria-label={t('NLSFamilySubTabMeetings')}
          data-testid="family-meetings-section"
        >
          {ptmDaysError && (
            <div className="familyClassTab__error" role="alert">
              {ptmDaysError}
            </div>
          )}

          {loadingPtmDays && ptmDays.length === 0 && (
            <div className="familyClassTab__loader" aria-label={t('NLSFamilyEventsLoading')}>
              <MiniLoader />
            </div>
          )}

          {!loadingPtmDays && ptmDays.length === 0 && !ptmDaysError && (
            <div className="familyClassTab__emptyState">
              <div className="familyClassTab__emptyTitle">
                {t('NLSFamilyPtmEmptyDays')}
              </div>
            </div>
          )}

          {ptmDays.length > 0 && (
            <div className="familyClassTab__cardList">
              {ptmDays.map((day) => {
                const effectiveTz = day.timezone || DEFAULT_EDU_TIMEZONE
                const openParts = utcIsoToZonedParts(day.bookingOpenAt, effectiveTz)
                const closeParts = utcIsoToZonedParts(day.bookingCloseAt, effectiveTz)
                const cutoffParts = utcIsoToZonedParts(day.rescheduleCutoffAt, effectiveTz)
                const isOnline = day.meetingType === 'ONLINE'

                return (
                  <div
                    key={day.id}
                    className="familyClassTab__card familyClassTab__eventCard"
                    onClick={() => setSelectedPtmDay(day)}
                    data-testid={`ptm-day-card-${day.id}`}
                  >
                    <div className="familyClassTab__cardHeader">
                      <div className="familyClassTab__eventRowTop">
                        <span className="familyClassTab__eventTime">
                          {formatEduDate(day.date, locale, effectiveTz)}
                        </span>
                        {day.status === 'CANCELLED' && (
                          <StatusChip tone="danger" size="sm">
                            {t('NLSFamilyEventCancelled')}
                          </StatusChip>
                        )}
                      </div>
                      <h4 className="familyClassTab__cardTitle">{day.title}</h4>
                      {day.status === 'CANCELLED' && day.cancelReason && (
                        <div className="familyClassTab__cancelReason">
                          {day.cancelReason}
                        </div>
                      )}
                    </div>

                    <div className="familyEventDetail__metaGrid" style={{ marginTop: 8 }}>
                      <div className="familyEventDetail__metaItem">
                        <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmMeetingTypeLabel')}</span>
                        <span className="familyEventDetail__metaValue">
                          {isOnline ? t('NLSEduPtmMeetingTypeOnline') : t('NLSEduPtmMeetingTypeInPerson')}
                        </span>
                      </div>
                      <div className="familyEventDetail__metaItem">
                        <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmRoomLabel')}</span>
                        <span className="familyEventDetail__metaValue">
                          {day.location || (isOnline ? 'Online' : 'School')}
                        </span>
                      </div>
                      <div className="familyEventDetail__metaItem">
                        <span className="familyEventDetail__metaLabel">{t('NLSEduPtmDayBookingOpenLabel')}</span>
                        <span className="familyEventDetail__metaValue">
                          {t('NLSFamilyPtmBookingOpenRange', {
                            open: openParts.date,
                            close: closeParts.date,
                          })}
                        </span>
                      </div>
                      <div className="familyEventDetail__metaItem">
                        <span className="familyEventDetail__metaLabel">{t('NLSEduPtmDayCutoffLabel')}</span>
                        <span className="familyEventDetail__metaValue">
                          {t('NLSFamilyPtmCutoffLabel', {
                            cutoff: `${cutoffParts.date} ${cutoffParts.time}`,
                          })}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedPtmDay(day)
                        }}
                        data-testid={`ptm-book-day-btn-${day.id}`}
                      >
                        {t('NLSFamilyPtmBookSlot')}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {selectedPtmDay && (
            <FamilyPtmBookingDialog
              isOpen={Boolean(selectedPtmDay)}
              onClose={() => setSelectedPtmDay(null)}
              workspaceId={workspaceId}
              learnerId={learnerId}
              day={selectedPtmDay}
              learner={learner}
              otherLearnerBookings={otherLearnerBookings}
              otherLearners={otherLearners}
              onBookingChanged={() => {
                setPtmReloadSeq((s) => s + 1)
              }}
            />
          )}
        </section>
      )}
      <AppDialog
        open={showConsentPrompt}
        title={t('NLSFamilyMediaConsentPromptTitle')}
        size="md"
        onDismiss={handleDecideLater}
        actions={[
          {
            label: t('NLSFamilyMediaConsentPromptDecideLater'),
            variant: 'secondary',
            onClick: handleDecideLater,
          },
          {
            label: savingConsentPrompt
              ? t('NLSFamilyMediaConsentPromptSaving')
              : t('NLSFamilyMediaConsentPromptSave'),
            variant: 'primary',
            disabled: !selectedPromptConsent || savingConsentPrompt,
            onClick: () => void handleSaveConsentPrompt(),
          },
        ]}
      >
        <div className="familyClassTab__consentPromptContent" data-testid="media-consent-prompt">
          <p className="familyClassTab__consentPromptBody">
            {t('NLSFamilyMediaConsentPromptBody')}
          </p>
          {consentPromptError && (
            <div className="familyClassTab__error" role="alert" data-testid="consent-prompt-error">
              {consentPromptError}
            </div>
          )}
          <div
            className="familyClassTab__consentRadioGroup"
            role="radiogroup"
            aria-label={t('NLSFamilyMediaConsentPromptTitle')}
          >
            <div className="familyClassTab__consentRadioItem">
              <Radio
                id="media-consent-radio-photos-ok"
                name="family-media-consent-prompt"
                value="PHOTOS_OK"
                checked={selectedPromptConsent === 'PHOTOS_OK'}
                onChange={() => setSelectedPromptConsent('PHOTOS_OK')}
                label={t('NLSFamilyMediaConsentPhotosOk')}
              />
              <div className="familyClassTab__consentRadioDesc">
                {t('NLSFamilyMediaConsentPhotosOkDesc')}
              </div>
            </div>
            <div className="familyClassTab__consentRadioItem">
              <Radio
                id="media-consent-radio-class-only"
                name="family-media-consent-prompt"
                value="CLASS_ONLY"
                checked={selectedPromptConsent === 'CLASS_ONLY'}
                onChange={() => setSelectedPromptConsent('CLASS_ONLY')}
                label={t('NLSFamilyMediaConsentClassOnly')}
              />
              <div className="familyClassTab__consentRadioDesc">
                {t('NLSFamilyMediaConsentClassOnlyDesc')}
              </div>
            </div>
            <div className="familyClassTab__consentRadioItem">
              <Radio
                id="media-consent-radio-none"
                name="family-media-consent-prompt"
                value="NONE"
                checked={selectedPromptConsent === 'NONE'}
                onChange={() => setSelectedPromptConsent('NONE')}
                label={t('NLSFamilyMediaConsentNone')}
              />
              <div className="familyClassTab__consentRadioDesc">
                {t('NLSFamilyMediaConsentNoneDesc')}
              </div>
            </div>
          </div>
        </div>
      </AppDialog>
    </div>
  )
}

export default FamilyClassTab
